import { Button } from '@chakra-ui/button'
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
} from '@chakra-ui/form-control'
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Icon,
  Text,
} from '@chakra-ui/react'
import type { FieldProps } from 'formik'
import { Field, Formik } from 'formik'
import React, { useContext, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { HiOutlineFolderRemove } from 'react-icons/hi'
import useFetch, { CachePolicies } from 'use-http'
import { IKReactSelect, colors } from '../../../ui-kit'
import useToast from '../../hooks/useToast'
import type {
  FileDetailsResponse,
  GetMediaCollectionsWithPermissionErrorResponse,
  GetMediaCollectionsWithPermissionResponse,
  RemoveAssetsFromMediaCollectionsSuccessResponse,
  RemoveMultipleAssetsFromMediaCollectionsErrorResponse,
  MediaLibrarySearchFileResponse,
} from '../../interfaces'
import { MLContext, UserContext } from '../../providers'
import { MediaLibraryActions } from '../../providers/MLProvider'

interface Props {
  isOpen: boolean
  onClose(): void
  fileDetails? :FileDetailsResponse
}

interface FormProps {
  mediaCollections: Array<{
    label: string
    value: string
  }>
}

const RemoveAssetFromMultipleMediaCollections = ({
  isOpen,
  onClose,
  fileDetails,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const toast = useToast()
  const { request, loading } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  let [
    { selectedFiles, selectedFolders, mediaCollectionRoute, refetch },
    dispatch,
  ] = useContext(MLContext)

  if (fileDetails) {
    selectedFiles = [fileDetails as unknown as MediaLibrarySearchFileResponse]
    selectedFolders = []
  }

  const [formError, setFormError] = useState<string>('')
  const [mediaCollectionOptions, setMediaCollectionOptions] = useState<
    Array<{
      label: string
      value: string
    }>
  >([])
  const [mediaCollections, setMediaCollections] = useState<
    Array<{
      label: string
      value: string
    }>
  >([])

  const assetIds: string[] = [
    ...selectedFiles.map(file => file.fileId),
    ...selectedFolders.map(folder => folder.folderId),
  ]

  const getMediaCollections = async (): Promise<void> => {
    if (user?.clientNumber && isOpen) {
      setFormError('')
      setMediaCollectionOptions([])
      setMediaCollections([])
      const res = (await request.get(
        `/api/clients/${user.clientNumber}/access-control/assets/${assetIds[0]}/media-collections?permission=["MANAGE"]`,
      )) as
        | GetMediaCollectionsWithPermissionResponse[]
        | GetMediaCollectionsWithPermissionErrorResponse

      if (request.error) {
        setFormError(
          (res as GetMediaCollectionsWithPermissionErrorResponse).message ??
            t(
              'error_in_feting_media_collections',
              'Error in fetching media collections. Please try again after sometime.',
            ),
        )
        return
      }

      setMediaCollectionOptions(
        (res as GetMediaCollectionsWithPermissionResponse[]).map(item => ({
          label: item.name,
          value: item.mediaCollectionId,
        })),
      )
    }
  }

  useEffect(() => {
    void getMediaCollections()
  }, [isOpen])

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose()
        setFormError('')
        setMediaCollections([])
      }}
      size="lg"
    >
      <ModalOverlay />
      <ModalContent>
        <Formik
          initialValues={{
            mediaCollections: [] as Array<{
              label: string
              value: string
            }>,
          }}
          onSubmit={async (values): Promise<void> => {
            setFormError('')

            if (values.mediaCollections.length > 100) {
              setFormError(
                t(
                  'remove_asset_from_media_collection_limit_exceeded_error',
                  'You cannot remove an asset from more than 100 media collections at a time.',
                ),
              )
              return
            }

            const mediaCollectionIds = values.mediaCollections.map(
              mediaCollection => mediaCollection.value,
            )
            const res = (await request.post(
              '/api/v2/media-collections/remove-assets',
              {
                mediaCollectionIds,
                assetIds,
              },
            )) as
              | RemoveAssetsFromMediaCollectionsSuccessResponse
              | RemoveMultipleAssetsFromMediaCollectionsErrorResponse
            if (request.error) {
              setFormError(
                (res as RemoveMultipleAssetsFromMediaCollectionsErrorResponse)
                  .message ??
                  t(
                    'something_went_wrong',
                    'Internal Error,Please contact us at support@imagekit.io for support.',
                  ),
              )
              return
            }
            setMediaCollections([])
            onClose()

            window.trackEvent({
              action: 'Submit',
              category: 'Media collection',
              label: 'Asset removed - media collection count',
              value: mediaCollectionIds.length,
            })

            let successfulMessage
            if (
              mediaCollectionIds.length === 1 &&
              (res as RemoveAssetsFromMediaCollectionsSuccessResponse)
                .successfullyUpdatedMediaCollectionsId.length === 1
            ) {
              const collectionName = values.mediaCollections[0]?.label

              successfulMessage = (
                <Trans t={t} i18nKey="collection_removed_1">
                  Asset(s) removed succesfully from {{ collectionName }}.
                </Trans>
              )
            } else if (
              values.mediaCollections.length === 2 &&
              (res as RemoveAssetsFromMediaCollectionsSuccessResponse)
                .successfullyUpdatedMediaCollectionsId.length === 2
            ) {
              const collectionName1 = values.mediaCollections[0]?.label
              const collectionName2 = values.mediaCollections[1]?.label

              successfulMessage = (
                <Trans t={t} i18nKey="collection_removed_2">
                  Asset(s) removed succesfully from {{ collectionName1 }} and{' '}
                  {{ collectionName2 }}.
                </Trans>
              )
            } else if (
              values.mediaCollections.length > 2 &&
              (res as RemoveAssetsFromMediaCollectionsSuccessResponse)
                .successfullyUpdatedMediaCollectionsId.length > 2
            ) {
              const collectionName1 = values.mediaCollections[0]?.label
              const collectionName2 = values.mediaCollections[1]?.label
              const remainingSize = (
                (res as RemoveAssetsFromMediaCollectionsSuccessResponse)
                  .successfullyUpdatedMediaCollectionsId.length - 2
              ).toString()

              successfulMessage = (
                <Trans t={t} i18nKey="collection_removed_multi">
                  Asset(s) removed succesfully from {{ collectionName1 }},{' '}
                  {{ collectionName2 }} and {{ remainingSize }}
                  other media collection(s).
                </Trans>
              )
            } else {
              successfulMessage = (
                <Trans t={t} i18nKey="media_collections_updated_successfully">
                  Media collection(s) updated succesfully.
                </Trans>
              )
            }

            toast({
              description: successfulMessage,
              status: 'success',
              isClosable: true,
              duration: 5000,
              position: 'top-right',
              variant: 'outline',
            })
          }}
        >
          {({ isSubmitting, dirty, submitForm, setFieldValue }) => (
            <>
              <ModalHeader>
                <Icon
                  as={HiOutlineFolderRemove}
                  color={colors.appBattleshipGrey}
                  backgroundColor={colors.appPaleGrey}
                  boxSize={6}
                  padding="0.1em"
                  borderRadius="50%"
                  marginRight="0.5em"
                />
                {t(
                  'remove_asset_from_media_collections',
                  'Remove from media collections',
                )}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                {formError ? (
                  <Alert status="error" mb="1em">
                    <AlertIcon />
                    <AlertDescription>
                      <Text lineHeight="1.2em">{formError}</Text>
                    </AlertDescription>
                  </Alert>
                ) : null}
                <Field name="mediaCollections">
                  {({ form }: FieldProps<string, FormProps>) => (
                    <FormControl
                      marginTop="0.3em"
                      isInvalid={Boolean(form.errors.mediaCollections)}
                    >
                      <FormLabel>
                        {t(
                          'media_collections_to_remove',
                          'Media collections to remove',
                        )}
                      </FormLabel>
                      <IKReactSelect
                        variant="compact"
                        captureMenuScroll={false}
                        menuPlacement="auto"
                        isClearable
                        isSearchable
                        isMulti={true}
                        closeMenuOnSelect={false}
                        stylesVariant="media_collection_modal_custom"
                        placeholder={t(
                          'select_media_collections',
                          'Select media collections',
                        )}
                        options={mediaCollectionOptions}
                        value={mediaCollections}
                        onChange={mediaCollectionField => {
                          setFieldValue(
                            'mediaCollections',
                            mediaCollectionField,
                          )
                          setMediaCollections(mediaCollectionField)
                        }}
                        menuPosition="fixed"
                        menuShouldScrollIntoView
                      />
                      {form.errors.mediaCollections ? (
                        <FormErrorMessage mt="0.25em">
                          {form.errors.mediaCollections}
                        </FormErrorMessage>
                      ) : (
                        <Box height="1.55em" />
                      )}
                    </FormControl>
                  )}
                </Field>
              </ModalBody>
              <ModalFooter
                borderTop={`1px solid ${colors.appGrey}`}
                style={{ gap: '1em' }}
              >
                <Button
                  onClick={() => {
                    onClose()
                    setFormError('')
                    setMediaCollections([])
                  }}
                  variant="outline"
                >
                  {t('cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  isDisabled={loading || !dirty}
                  onClick={() => {
                    void submitForm()
                  }}
                >
                  {t('submit', 'Submit')}
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default RemoveAssetFromMultipleMediaCollections
